import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import CameraAltRoundedIcon from '@mui/icons-material/CameraAltRounded'
import CloseIcon from '@mui/icons-material/Close'
import HeadsetMicRoundedIcon from '@mui/icons-material/HeadsetMicRounded'
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import LoginRoundedIcon from '@mui/icons-material/LoginRounded'
import MicRoundedIcon from '@mui/icons-material/MicRounded'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'

import { executeRecaptchaV3 } from 'src/views/applyPublic/recaptchaV3'
import { preflightPublicSessionJoin } from 'src/consumer/services/featureServices'
import { APP_CONSTANTS } from 'src/consumer/constants'
import { SEOComponent } from 'src/consumer/helpers/utilFunctions'
import SupportQRCode from 'src/consumer/components/common/SupportQRCode'
import { useContactNumber } from 'src/consumer/components/AumhumSite/useContactNumber'
import { normalizeAuthorities } from 'src/shared/auth/loginHandoff'

const ClubbedAuthModal = React.lazy(() =>
  import('src/consumer/components/SignIn/ClubbedAuthComponents').then((module) => ({
    default: module.ClubbedAuthModal,
  })),
)

const AskCameraMicPermissionsDialog = React.lazy(() =>
  import('src/consumer/components/PublicLiveSessionPage/components/AskCameraMicPermissionsDialog'),
)

const SESSION_REFERENCE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{2,95}$/
const ACCESS_CODE_PATTERN = /^[A-Za-z0-9_-]{1,32}$/
const SESSION_LIST_PATH = '/my-live-sessions/booked-sessions'
const PATIENT_SESSION_LIST_PATH = '/patient-home/sessions/upcoming'
const DEFAULT_LANGUAGE = 'en'

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'हिन्दी' },
  { value: 'kn', label: 'ಕನ್ನಡ' },
]

const PAGE_COPY = {
  en: {
    login: 'Login',
    language: 'Language',
    title: 'Join your session',
    intro: 'Enter the Session ID from your AUMHUM invite.',
    sessionIdLabel: 'Session ID',
    sessionIdPlaceholder: 'Enter Session ID or paste invite link',
    accessCodeLabel: 'Session Code',
    accessCodePlaceholder: 'Enter Session Code',
    legalPrefix: 'By joining, you agree to AUMHUM',
    terms: 'Terms of Service',
    and: 'and',
    privacy: 'Privacy Policy',
    submit: 'Join session',
    submitting: 'Checking session',
    recaptcha: 'Protected by secure verification.',
    patientAccountError:
      'This login does not look like a patient account. You can still join here with your Session ID, or contact AUMHUM support.',
    invalidSessionId: 'Enter a valid Session ID from your AUMHUM invite.',
    invalidAccessCode: 'Enter the Session Code from your invite.',
    codeDidNotWork: 'That Session Code did not work.',
    codeRequired: 'This session needs the Session Code from your invite.',
    captchaError: 'We could not verify this request. Please refresh and try again.',
    notFound: 'We could not find this session. Please check your invite.',
    rateLimited: 'Please wait a moment before trying again.',
    joinError: 'Unable to join right now. Please try again or contact AUMHUM support.',
    helpTooltip: 'Help joining',
    help: {
      eyebrow: 'AUMHUM support',
      title: 'Joining help',
      safety:
        'Keep your Session ID and Session Code private. AUMHUM support will never ask for your password.',
      permissionsTitle: 'Camera and microphone',
      permissionsBody:
        'If your browser blocks camera or microphone access, open the permission guide before your session starts.',
      permissionsButton: 'Check camera and mic permissions',
      permissionsChecking: 'Checking permissions',
      permissionsReady: 'Camera and microphone permissions are already allowed on this browser.',
      permissionsUnavailable:
        'This browser could not report camera and microphone access. Please try from Chrome, Safari, or Edge.',
      permissionsSkipped:
        'You can still join, but audio or video may not work until camera and microphone access is allowed.',
      supportTitle: 'Human support',
      supportBody: 'Call or WhatsApp the same support number if anything feels stuck.',
      qrTitle: 'WhatsApp support',
      qrSubtitle: 'Scan to start a secure support chat',
      whatsapp: 'WhatsApp',
      call: 'Call',
    },
    seoDescription:
      'Securely join your AUMHUM care session using the Session ID and optional Session Code from your invite.',
  },
  hi: {
    login: 'लॉगिन',
    language: 'भाषा',
    title: 'अपना session join करें',
    intro: 'अपने AUMHUM invite में दिया गया Session ID डालें.',
    sessionIdLabel: 'Session ID',
    sessionIdPlaceholder: 'Session ID डालें या invite link paste करें',
    accessCodeLabel: 'Session Code',
    accessCodePlaceholder: 'Session Code डालें',
    legalPrefix: 'Join करने पर आप AUMHUM के',
    terms: 'Terms of Service',
    and: 'और',
    privacy: 'Privacy Policy',
    submit: 'Session join करें',
    submitting: 'Session check हो रहा है',
    recaptcha: 'Secure verification से protected.',
    patientAccountError:
      'यह login patient account जैसा नहीं दिख रहा है. आप Session ID से यहीं join कर सकते हैं या AUMHUM support से संपर्क करें.',
    invalidSessionId: 'AUMHUM invite में दिया गया valid Session ID डालें.',
    invalidAccessCode: 'Invite में दिया गया Session Code डालें.',
    codeDidNotWork: 'यह Session Code काम नहीं कर रहा.',
    codeRequired: 'इस session के लिए invite वाला Session Code चाहिए.',
    captchaError: 'हम यह request verify नहीं कर पाए. Refresh करके फिर कोशिश करें.',
    notFound: 'यह session नहीं मिला. कृपया invite check करें.',
    rateLimited: 'कृपया कुछ पल रुककर फिर कोशिश करें.',
    joinError: 'अभी join नहीं हो पा रहा. फिर कोशिश करें या AUMHUM support से संपर्क करें.',
    helpTooltip: 'Join करने में मदद',
    help: {
      eyebrow: 'AUMHUM support',
      title: 'Joining help',
      safety:
        'अपना Session ID और Session Code private रखें. AUMHUM support आपका password कभी नहीं मांगेगा.',
      permissionsTitle: 'Camera और microphone',
      permissionsBody:
        'अगर browser camera या microphone block करता है, session शुरू होने से पहले permission guide खोलें.',
      permissionsButton: 'Camera और mic permissions check करें',
      permissionsChecking: 'Permissions check हो रही हैं',
      permissionsReady: 'इस browser पर camera और microphone permissions already allowed हैं.',
      permissionsUnavailable:
        'यह browser camera और microphone access report नहीं कर पाया. कृपया Chrome, Safari या Edge से try करें.',
      permissionsSkipped:
        'आप फिर भी join कर सकते हैं, लेकिन camera और microphone access allow होने तक audio या video काम नहीं कर सकते.',
      supportTitle: 'Human support',
      supportBody: 'अगर कुछ अटका हुआ लगे, इसी support number पर call या WhatsApp करें.',
      qrTitle: 'WhatsApp support',
      qrSubtitle: 'Secure support chat शुरू करने के लिए scan करें',
      whatsapp: 'WhatsApp',
      call: 'Call',
    },
    seoDescription:
      'अपने AUMHUM care session को Session ID और optional Session Code के साथ securely join करें.',
  },
  kn: {
    login: 'ಲಾಗಿನ್',
    language: 'ಭಾಷೆ',
    title: 'ನಿಮ್ಮ session join ಮಾಡಿ',
    intro: 'ನಿಮ್ಮ AUMHUM invite ನಲ್ಲಿ ಇರುವ Session ID ನಮೂದಿಸಿ.',
    sessionIdLabel: 'Session ID',
    sessionIdPlaceholder: 'Session ID ನಮೂದಿಸಿ ಅಥವಾ invite link paste ಮಾಡಿ',
    accessCodeLabel: 'Session Code',
    accessCodePlaceholder: 'Session Code ನಮೂದಿಸಿ',
    legalPrefix: 'Join ಮಾಡುವುದರಿಂದ ನೀವು AUMHUM ನ',
    terms: 'Terms of Service',
    and: 'ಮತ್ತು',
    privacy: 'Privacy Policy',
    submit: 'Session join ಮಾಡಿ',
    submitting: 'Session check ಆಗುತ್ತಿದೆ',
    recaptcha: 'Secure verification ಮೂಲಕ protected.',
    patientAccountError:
      'ಈ login patient account ಆಗಿ ಕಾಣುತ್ತಿಲ್ಲ. Session ID ಬಳಸಿ ಇಲ್ಲಿಂದಲೇ join ಮಾಡಬಹುದು ಅಥವಾ AUMHUM support ಸಂಪರ್ಕಿಸಿ.',
    invalidSessionId: 'AUMHUM invite ನಲ್ಲಿ ಇರುವ valid Session ID ನಮೂದಿಸಿ.',
    invalidAccessCode: 'Invite ನಲ್ಲಿ ಇರುವ Session Code ನಮೂದಿಸಿ.',
    codeDidNotWork: 'ಈ Session Code ಕೆಲಸ ಮಾಡಲಿಲ್ಲ.',
    codeRequired: 'ಈ session ಗೆ invite ನಲ್ಲಿ ಇರುವ Session Code ಬೇಕು.',
    captchaError: 'ಈ request verify ಆಗಲಿಲ್ಲ. Refresh ಮಾಡಿ ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    notFound: 'ಈ session ಸಿಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು invite ಪರಿಶೀಲಿಸಿ.',
    rateLimited: 'ದಯವಿಟ್ಟು ಸ್ವಲ್ಪ ಹೊತ್ತಿನ ನಂತರ ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    joinError: 'ಈಗ join ಆಗುತ್ತಿಲ್ಲ. ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ ಅಥವಾ AUMHUM support ಸಂಪರ್ಕಿಸಿ.',
    helpTooltip: 'Join ಮಾಡಲು ಸಹಾಯ',
    help: {
      eyebrow: 'AUMHUM support',
      title: 'Joining help',
      safety:
        'ನಿಮ್ಮ Session ID ಮತ್ತು Session Code private ಆಗಿಡಿ. AUMHUM support ನಿಮ್ಮ password ಕೇಳುವುದಿಲ್ಲ.',
      permissionsTitle: 'Camera ಮತ್ತು microphone',
      permissionsBody:
        'Browser camera ಅಥವಾ microphone block ಮಾಡಿದರೆ, session ಶುರುವಾಗುವ ಮೊದಲು permission guide ತೆರೆಯಿರಿ.',
      permissionsButton: 'Camera ಮತ್ತು mic permissions check ಮಾಡಿ',
      permissionsChecking: 'Permissions check ಆಗುತ್ತಿದೆ',
      permissionsReady: 'ಈ browser ನಲ್ಲಿ camera ಮತ್ತು microphone permissions ಈಗಾಗಲೇ allowed ಆಗಿವೆ.',
      permissionsUnavailable:
        'ಈ browser camera ಮತ್ತು microphone access report ಮಾಡಲಿಲ್ಲ. ದಯವಿಟ್ಟು Chrome, Safari ಅಥವಾ Edge ನಲ್ಲಿ ಪ್ರಯತ್ನಿಸಿ.',
      permissionsSkipped:
        'ನೀವು ಇನ್ನೂ join ಮಾಡಬಹುದು, ಆದರೆ camera ಮತ್ತು microphone access allow ಆಗುವವರೆಗೆ audio ಅಥವಾ video ಕೆಲಸ ಮಾಡದೇ ಇರಬಹುದು.',
      supportTitle: 'Human support',
      supportBody: 'ಯಾವುದಾದರೂ ಅಡಚಣೆ ಇದ್ದರೆ ಇದೇ support number ಗೆ call ಅಥವಾ WhatsApp ಮಾಡಿ.',
      qrTitle: 'WhatsApp support',
      qrSubtitle: 'Secure support chat ಆರಂಭಿಸಲು scan ಮಾಡಿ',
      whatsapp: 'WhatsApp',
      call: 'Call',
    },
    seoDescription:
      'ನಿಮ್ಮ AUMHUM care session ಅನ್ನು Session ID ಮತ್ತು optional Session Code ಬಳಸಿ securely join ಮಾಡಿ.',
  },
}

function normalizeAccessCode(value) {
  return String(value || '').trim().replace(/\s+/g, '')
}

function extractSessionAccessCode(searchParams) {
  const candidate =
    searchParams.get('code') ||
    searchParams.get('otp') ||
    searchParams.get('key') ||
    searchParams.get('shortKey')
  return normalizeAccessCode(candidate)
}

function parseSessionInviteInput(value) {
  const trimmed = String(value || '').trim()
  if (!trimmed) return { sessionReference: '', accessCode: '' }

  const parseUrl = (urlValue) => {
    const match = urlValue.pathname.match(/\/v\/([^/?#]+)/)
    if (!match?.[1]) return null
    return {
      sessionReference: decodeURIComponent(match[1]).trim(),
      accessCode: extractSessionAccessCode(urlValue.searchParams),
    }
  }

  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('/')) {
    try {
      const parsed = new URL(
        trimmed,
        typeof window === 'undefined' ? 'https://aumhum.com' : window.location.origin,
      )
      const result = parseUrl(parsed)
      if (result) return result
    } catch (_) {
      // The field also accepts a plain Session ID.
    }
  }

  return {
    sessionReference: trimmed
      .replace(/^\/+/, '')
      .replace(/^v\//i, '')
      .split(/[?#]/)[0]
      .trim(),
    accessCode: '',
  }
}

function getSessionListPathForAuthorities(authoritiesLike) {
  const authorities = normalizeAuthorities(authoritiesLike)
  if (authorities.includes('ROLE_PATIENT')) return PATIENT_SESSION_LIST_PATH
  if (authorities.includes('ROLE_USER')) return SESSION_LIST_PATH
  return null
}

function buildSupportWhatsappUrl(contact) {
  const message = encodeURIComponent('I need help joining my AUMHUM session.')
  return `${contact.whatsappHref}?text=${message}`
}

async function queryMediaPermissionState(permissionName) {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) return 'prompt'

  try {
    const permission = await navigator.permissions.query({ name: permissionName })
    return permission?.state || 'prompt'
  } catch (_) {
    return 'prompt'
  }
}

async function getMediaPermissionCheckResult() {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return 'unavailable'
  }

  const [microphoneState, cameraState] = await Promise.all([
    queryMediaPermissionState('microphone'),
    queryMediaPermissionState('camera'),
  ])

  if (microphoneState === 'granted' && cameraState === 'granted') return 'granted'
  if (microphoneState === 'denied' || cameraState === 'denied') return 'blocked'
  return 'needs-guide'
}

function LegalDrawer({ open, type, onClose }) {
  const [loadedContent, setLoadedContent] = useState({})
  const title = type === 'terms' ? 'Terms of Service' : 'Privacy Policy'
  const src = type === 'terms' ? APP_CONSTANTS.APP_TERMS_URL : APP_CONSTANTS.APP_PRIVACY_POLICY_URL
  const showLoader = open && !loadedContent[type]

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '90%', md: '620px', lg: '720px' },
          maxWidth: '100%',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: { xs: '16px', md: '20px 24px' },
            borderBottom: '1px solid #e5e7eb',
            background: '#ffffff',
          }}
        >
          <Typography sx={{ fontSize: { xs: '18px', md: '20px' }, fontWeight: 800 }}>
            {title}
          </Typography>
          <IconButton aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ position: 'relative', flex: 1, overflow: 'auto', background: '#ffffff' }}>
          {showLoader && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                display: 'grid',
                placeItems: 'center',
                background: '#ffffff',
              }}
            >
              <Box sx={{ display: 'grid', justifyItems: 'center', gap: 1.5 }}>
                <CircularProgress size={28} sx={{ color: '#6d28d9' }} />
                <Typography sx={{ color: '#64748b', fontSize: 14, fontWeight: 700 }}>
                  Loading {title.toLowerCase()}...
                </Typography>
              </Box>
            </Box>
          )}
          <iframe
            src={src}
            title={title}
            onLoad={() =>
              setLoadedContent((current) => ({
                ...current,
                [type]: true,
              }))
            }
            style={{
              width: '100%',
              height: '100%',
              minHeight: 'calc(100vh - 78px)',
              border: 'none',
            }}
          />
        </Box>
      </Box>
    </Drawer>
  )
}

function HelpDrawer({
  open,
  onClose,
  contact,
  supportQrUrl,
  supportWhatsappUrl,
  onOpenPermissions,
  permissionCheckStatus,
  checkingMediaPermissions,
  copy,
}) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 408 },
          maxWidth: '100%',
          borderRadius: { xs: 0, sm: '20px 0 0 20px' },
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          background: '#f8fafc',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: { xs: '18px 18px 14px', sm: '22px 24px 16px' },
            background: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <Box>
            <Typography sx={{ color: '#475569', fontSize: 13, fontWeight: 700 }}>
              {copy.help.eyebrow}
            </Typography>
            <Typography sx={{ color: '#0f172a', fontSize: 22, fontWeight: 850 }}>
              {copy.help.title}
            </Typography>
          </Box>
          <IconButton aria-label="Close help" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', padding: { xs: '20px 18px', sm: '24px' } }}>
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              alignItems: 'flex-start',
              padding: '16px',
              border: '1px solid #dbeafe',
              borderRadius: '16px',
              background: '#eff6ff',
              color: '#1e3a8a',
            }}
          >
            <ShieldOutlinedIcon sx={{ mt: 0.2 }} />
            <Typography sx={{ fontSize: 14, lineHeight: 1.55 }}>
              {copy.help.safety}
            </Typography>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography sx={{ color: '#0f172a', fontSize: 16, fontWeight: 800 }}>
              {copy.help.permissionsTitle}
            </Typography>
            <Typography sx={{ mt: 0.75, color: '#64748b', fontSize: 14, lineHeight: 1.6 }}>
              {copy.help.permissionsBody}
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              onClick={onOpenPermissions}
              disabled={checkingMediaPermissions}
              startIcon={
                <Box sx={{ display: 'flex', gap: 0.3 }}>
                  <CameraAltRoundedIcon fontSize="small" />
                  <MicRoundedIcon fontSize="small" />
                </Box>
              }
              endIcon={
                checkingMediaPermissions ? (
                  <CircularProgress size={16} sx={{ color: 'inherit' }} />
                ) : null
              }
              sx={{
                mt: 1.5,
                justifyContent: 'flex-start',
                borderRadius: '12px',
                borderColor: '#cbd5e1',
                color: '#0f172a',
                fontWeight: 800,
                py: 1.15,
                textTransform: 'none',
                '& .MuiButton-endIcon': { ml: 'auto' },
              }}
            >
              {checkingMediaPermissions
                ? copy.help.permissionsChecking
                : copy.help.permissionsButton}
            </Button>
            {permissionCheckStatus && (
              <Alert
                severity={permissionCheckStatus.severity}
                sx={{
                  mt: 1.5,
                  borderRadius: '12px',
                  '& .MuiAlert-message': {
                    color: '#334155',
                    fontSize: 13,
                    lineHeight: 1.45,
                    fontWeight: 700,
                  },
                }}
              >
                {permissionCheckStatus.message}
              </Alert>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HeadsetMicRoundedIcon sx={{ color: '#7c3aed' }} />
              <Typography sx={{ color: '#0f172a', fontSize: 16, fontWeight: 800 }}>
                {copy.help.supportTitle}
              </Typography>
            </Box>
            <Typography sx={{ mt: 0.75, color: '#64748b', fontSize: 14, lineHeight: 1.6 }}>
              {copy.help.supportBody}
            </Typography>

            <Box
              sx={{
                mt: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
                padding: '18px',
                borderRadius: '18px',
                background: '#ffffff',
                border: '1px solid #e2e8f0',
              }}
            >
              <SupportQRCode
                qrCodeUrl={supportQrUrl}
                title={copy.help.qrTitle}
                subtitle={copy.help.qrSubtitle}
                size="medium"
              />
              <Typography sx={{ color: '#334155', fontSize: 15, fontWeight: 800 }}>
                {contact.display}
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mt: 1.5 }}>
              <Button
                component="a"
                href={supportWhatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                startIcon={<WhatsAppIcon />}
                sx={{
                  borderRadius: '12px',
                  background: '#16a34a',
                  color: '#ffffff',
                  fontWeight: 850,
                  py: 1.15,
                  textTransform: 'none',
                  '&:hover': { background: '#15803d' },
                }}
              >
                {copy.help.whatsapp}
              </Button>
              <Button
                component="a"
                href={contact.telHref}
                variant="outlined"
                startIcon={<PhoneOutlinedIcon />}
                sx={{
                  borderRadius: '12px',
                  borderColor: '#cbd5e1',
                  color: '#0f172a',
                  fontWeight: 850,
                  py: 1.15,
                  textTransform: 'none',
                }}
              >
                {copy.help.call}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Drawer>
  )
}

function PermissionDialogHost({ open, onClose, onPermissionsGranted, onSkipPermissions }) {
  const [microphoneError, setMicrophoneError] = useState(null)
  const [cameraError, setCameraError] = useState(null)
  const [availableMicrophones, setAvailableMicrophones] = useState([])
  const [selectedMicrophone, setSelectedMicrophone] = useState('')
  const [availableSpeakers, setAvailableSpeakers] = useState([])
  const [selectedSpeaker, setSelectedSpeaker] = useState('')
  const [availableCameras, setAvailableCameras] = useState([])
  const [selectedCamera, setSelectedCamera] = useState('')

  return (
    <Suspense fallback={null}>
      <AskCameraMicPermissionsDialog
        giveBothPermissionsDialogOpen={open}
        setGiveBothPermissionsDialogOpen={onClose}
        giveMicrophonePermissionDialogOpen={false}
        setGiveMicrophonePermissionDialogOpen={() => {}}
        giveCameraPermissionDialogOpen={false}
        setGiveCameraPermissionDialogOpen={() => {}}
        setMicrophoneError={setMicrophoneError}
        setCameraError={setCameraError}
        setAvailableMicrophones={setAvailableMicrophones}
        setSelectedMicrophone={setSelectedMicrophone}
        setAvailableSpeakers={setAvailableSpeakers}
        setSelectedSpeaker={setSelectedSpeaker}
        setAvailableCameras={setAvailableCameras}
        setSelectedCamera={setSelectedCamera}
        microphoneError={microphoneError}
        cameraError={cameraError}
        availableMicrophones={availableMicrophones}
        selectedMicrophone={selectedMicrophone}
        availableSpeakers={availableSpeakers}
        selectedSpeaker={selectedSpeaker}
        availableCameras={availableCameras}
        selectedCamera={selectedCamera}
        sessionType="session"
        onPermissionsGranted={onPermissionsGranted}
        onSkipPermissions={onSkipPermissions}
      />
    </Suspense>
  )
}

export default function PublicSessionCodeJoinPage() {
  const navigate = useNavigate()
  const contact = useContactNumber()
  const sessionInputRef = useRef(null)
  const accessCodeInputRef = useRef(null)
  const [language, setLanguage] = useState(DEFAULT_LANGUAGE)
  const [sessionReference, setSessionReference] = useState('')
  const [accessCode, setAccessCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [accessCodeRequired, setAccessCodeRequired] = useState(false)
  const [authOpen, setAuthOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false)
  const [checkingMediaPermissions, setCheckingMediaPermissions] = useState(false)
  const [permissionCheckStatus, setPermissionCheckStatus] = useState(null)
  const [legalDrawer, setLegalDrawer] = useState({ open: false, type: 'terms' })
  const copy = PAGE_COPY[language] || PAGE_COPY[DEFAULT_LANGUAGE]

  const normalizedSessionReference = useMemo(
    () => parseSessionInviteInput(sessionReference).sessionReference,
    [sessionReference],
  )
  const normalizedAccessCode = useMemo(() => normalizeAccessCode(accessCode), [accessCode])
  const showAccessCodeField = accessCodeRequired || Boolean(normalizedAccessCode)
  const supportWhatsappUrl = useMemo(() => buildSupportWhatsappUrl(contact), [contact])
  const supportQrUrl = useMemo(
    () =>
      `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=8&data=${encodeURIComponent(
        supportWhatsappUrl,
      )}`,
    [supportWhatsappUrl],
  )

  useEffect(() => {
    const focusTimer = window.setTimeout(() => {
      sessionInputRef.current?.focus?.()
    }, 80)
    return () => window.clearTimeout(focusTimer)
  }, [])

  const canSubmit =
    SESSION_REFERENCE_PATTERN.test(normalizedSessionReference) &&
    (!showAccessCodeField || ACCESS_CODE_PATTERN.test(normalizedAccessCode)) &&
    !loading

  function handleSessionReferenceChange(value) {
    const parsed = parseSessionInviteInput(value)
    setSessionReference(value)
    setError('')
    if (parsed.accessCode) {
      setAccessCode(parsed.accessCode)
      setAccessCodeRequired(true)
    } else if (!accessCodeRequired) {
      setAccessCode('')
    }
  }

  function openLegalDrawer(type) {
    setLegalDrawer({ open: true, type })
  }

  async function handleOpenPermissionsFromHelp() {
    setPermissionCheckStatus(null)
    setCheckingMediaPermissions(true)

    try {
      const permissionResult = await getMediaPermissionCheckResult()

      if (permissionResult === 'granted') {
        setPermissionCheckStatus({
          severity: 'success',
          message: copy.help.permissionsReady,
        })
        return
      }

      if (permissionResult === 'unavailable') {
        setPermissionCheckStatus({
          severity: 'warning',
          message: copy.help.permissionsUnavailable,
        })
        return
      }

      setHelpOpen(false)
      setPermissionDialogOpen(true)
    } catch (_) {
      setHelpOpen(false)
      setPermissionDialogOpen(true)
    } finally {
      setCheckingMediaPermissions(false)
    }
  }

  function handlePermissionsGrantedFromSheet() {
    setPermissionDialogOpen(false)
    setPermissionCheckStatus({
      severity: 'success',
      message: copy.help.permissionsReady,
    })
    setHelpOpen(true)
  }

  function handlePermissionsSkippedFromSheet() {
    setPermissionDialogOpen(false)
    setPermissionCheckStatus({
      severity: 'warning',
      message: copy.help.permissionsSkipped,
    })
    setHelpOpen(true)
  }

  function showPatientAccountMessage() {
    setError(copy.patientAccountError)
  }

  function goToLoggedInSessionList() {
    const targetPath = getSessionListPathForAuthorities(localStorage.getItem('authorities'))
    if (targetPath) {
      navigate(targetPath)
      return
    }
    showPatientAccountMessage()
  }

  function handleLoginClick() {
    if (localStorage.getItem('token')) {
      goToLoggedInSessionList()
      return
    }
    setAuthOpen(true)
  }

  async function handleAuthenticated() {
    setAuthOpen(false)
    const targetPath = getSessionListPathForAuthorities(localStorage.getItem('authorities'))
    if (targetPath) {
      navigate(targetPath)
      return
    }
    showPatientAccountMessage()
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setError('')

    if (!SESSION_REFERENCE_PATTERN.test(normalizedSessionReference)) {
      setError(copy.invalidSessionId)
      return
    }
    if (showAccessCodeField && !ACCESS_CODE_PATTERN.test(normalizedAccessCode)) {
      setError(copy.invalidAccessCode)
      return
    }

    setLoading(true)
    try {
      const recaptchaToken = await executeRecaptchaV3('public_session_preflight')
      const response = await preflightPublicSessionJoin({
        sessionSlug: normalizedSessionReference,
        shortKey: showAccessCodeField ? normalizedAccessCode : '',
        recaptchaToken,
      })

      if (response?.status === 200 && response?.data?.code === 2000) {
        const query = normalizedAccessCode
          ? `?code=${encodeURIComponent(normalizedAccessCode)}`
          : ''
        navigate(`/v/${encodeURIComponent(normalizedSessionReference)}${query}`)
        return
      }

      if (response?.data?.code === 4041 || response?.data?.data?.requiresShortKey) {
        setAccessCodeRequired(true)
        setError(
          normalizedAccessCode
            ? copy.codeDidNotWork
            : copy.codeRequired,
        )
        setTimeout(() => accessCodeInputRef.current?.focus?.(), 0)
        return
      }

      if (response?.data?.code === 8000) {
        setError(copy.captchaError)
        return
      }

      setError(response?.data?.message || copy.notFound)
    } catch (err) {
      const code = err?.response?.data?.code
      if (code === 4041 || err?.response?.data?.data?.requiresShortKey) {
        setAccessCodeRequired(true)
        setError(
          normalizedAccessCode
            ? copy.codeDidNotWork
            : copy.codeRequired,
        )
        setTimeout(() => accessCodeInputRef.current?.focus?.(), 0)
      } else if (code === 8000) {
        setError(copy.captchaError)
      } else if (code === 4290) {
        setError(copy.rateLimited)
      } else {
        setError(copy.joinError)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <SEOComponent
        title="Enter your session code | AUMHUM"
        description="Have an AUMHUM session invite? Enter your Session ID (and Session Code if shared) to join your video session from any device."
        image="https://media.aumhum.xyz/images/prod/MEDIA-d22a0e19f15448a6acfe855580c1833b"
        type="website"
        keywordsArr={[
          'AUMHUM session',
          'join AUMHUM session',
          'AUMHUM Session ID',
          'online therapy session',
          'online mental health session',
          'patient session login',
        ]}
      />
      <Box
        sx={{
          minHeight: '100vh',
          background: '#f8fafc',
          color: '#0f172a',
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
        }}
      >
        <Box
          component="header"
          sx={{
            width: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 1.5, md: 2.25 },
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: { xs: 1, sm: 1.25 },
              maxWidth: '100%',
            }}
          >
            <TextField
              select
              size="small"
              label={copy.language}
              value={language}
              onChange={(event) => setLanguage(event.target.value)}
              sx={{
                width: { xs: 126, sm: 146 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '999px',
                  background: '#ffffff',
                  boxShadow: '0 10px 26px rgba(15, 23, 42, 0.08)',
                  fontSize: 13,
                  fontWeight: 800,
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#cbd5e1' },
                  '&.Mui-focused fieldset': { borderColor: '#7c3aed' },
                },
                '& .MuiInputLabel-root': { fontSize: 13, fontWeight: 700 },
              }}
            >
              {LANGUAGE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <Button
              aria-label={copy.login}
              onClick={handleLoginClick}
              startIcon={<LoginRoundedIcon />}
              sx={{
                borderRadius: '999px',
                background: '#ffffff',
                color: '#312e81',
                border: '1px solid #e2e8f0',
                boxShadow: '0 10px 26px rgba(15, 23, 42, 0.08)',
                fontWeight: 850,
                px: { xs: 1.5, sm: 2.2 },
                minWidth: { xs: 44, sm: 112 },
                maxWidth: '100%',
                textTransform: 'none',
                '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.8 } },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                {copy.login}
              </Box>
            </Button>
          </Box>
        </Box>

        <Box
          component="main"
          sx={{
            flex: 1,
            width: '100%',
            boxSizing: 'border-box',
            display: 'grid',
            placeItems: { xs: 'start center', sm: 'center' },
            px: { xs: 2, sm: 3 },
            py: { xs: 1, md: 3 },
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 492,
              boxSizing: 'border-box',
              position: 'relative',
              mt: { xs: 3, sm: 2.25 },
              borderRadius: { xs: '22px', sm: '24px' },
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              boxShadow: '0 24px 64px rgba(15, 23, 42, 0.12)',
              p: { xs: 2, sm: 3 },
              pt: { xs: 4.5, sm: 4.75 },
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 44,
                height: 44,
                borderRadius: '14px',
                display: 'grid',
                placeItems: 'center',
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                boxShadow: '0 14px 30px rgba(15, 23, 42, 0.12)',
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '6px',
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="img"
                  src={APP_CONSTANTS.APP_LOGO_LINK}
                  alt="AUMHUM"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </Box>
            </Box>

            <Typography
              component="h1"
              sx={{
                color: '#0f172a',
                fontSize: { xs: 26, sm: 32 },
                lineHeight: 1.14,
                fontWeight: 900,
              }}
            >
              {copy.title}
            </Typography>
            <Typography
              sx={{
                mt: 0.9,
                color: '#475569',
                fontSize: { xs: 14.5, sm: 15.5 },
                lineHeight: 1.55,
              }}
            >
              {copy.intro}
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2.5 }}>
              <TextField
                fullWidth
                inputRef={sessionInputRef}
                label={copy.sessionIdLabel}
                value={sessionReference}
                onChange={(event) => handleSessionReferenceChange(event.target.value)}
                placeholder={copy.sessionIdPlaceholder}
                autoComplete="off"
                autoFocus
                inputProps={{
                  inputMode: 'text',
                  'aria-label': copy.sessionIdLabel,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VpnKeyOutlinedIcon sx={{ color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '16px',
                    background: '#ffffff',
                    '& fieldset': { borderColor: '#cbd5e1' },
                    '&:hover fieldset': { borderColor: '#94a3b8' },
                    '&.Mui-focused fieldset': { borderColor: '#7c3aed' },
                  },
                }}
              />

              {showAccessCodeField && (
                <TextField
                  fullWidth
                  inputRef={accessCodeInputRef}
                  label={copy.accessCodeLabel}
                  value={accessCode}
                  onChange={(event) => {
                    setAccessCode(event.target.value)
                    setError('')
                  }}
                  placeholder={copy.accessCodePlaceholder}
                  autoComplete="one-time-code"
                  inputProps={{
                    inputMode: 'text',
                    'aria-label': copy.accessCodeLabel,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ color: '#64748b' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mt: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '16px',
                      background: '#ffffff',
                      '& fieldset': { borderColor: '#cbd5e1' },
                      '&:hover fieldset': { borderColor: '#94a3b8' },
                      '&.Mui-focused fieldset': { borderColor: '#7c3aed' },
                    },
                  }}
                />
              )}

              {error && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: '14px' }}>
                  {error}
                </Alert>
              )}

              <Typography
                sx={{
                  mt: 2.25,
                  color: '#64748b',
                  fontSize: 13,
                  lineHeight: 1.55,
                }}
              >
                {copy.legalPrefix}{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={() => openLegalDrawer('terms')}
                  sx={{ color: '#5b21b6', fontWeight: 800, mx: 0.35, verticalAlign: 'baseline' }}
                >
                  {copy.terms}
                </Link>{' '}
                {copy.and}{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={() => openLegalDrawer('privacy')}
                  sx={{ color: '#5b21b6', fontWeight: 800, mx: 0.35, verticalAlign: 'baseline' }}
                >
                  {copy.privacy}
                </Link>
                .
              </Typography>

              <Button
                type="submit"
                fullWidth
                disabled={!canSubmit}
                endIcon={
                  loading ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : <ArrowForwardRoundedIcon />
                }
                sx={{
                  mt: 2.5,
                  minHeight: 52,
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #6d28d9 0%, #2563eb 100%)',
                  color: '#ffffff',
                  fontSize: 16,
                  fontWeight: 900,
                  boxShadow: '0 18px 38px rgba(79, 70, 229, 0.28)',
                  textTransform: 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5b21b6 0%, #1d4ed8 100%)',
                  },
                  '&.Mui-disabled': {
                    background: '#cbd5e1',
                    color: '#ffffff',
                    boxShadow: 'none',
                  },
                }}
              >
                {loading ? copy.submitting : copy.submit}
              </Button>

              <Typography
                sx={{
                  mt: 1.5,
                  color: '#94a3b8',
                  fontSize: 12,
                  textAlign: 'center',
                  lineHeight: 1.45,
                }}
              >
                {copy.recaptcha}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Tooltip title={copy.helpTooltip}>
          <IconButton
            aria-label={copy.helpTooltip}
            onClick={() => setHelpOpen(true)}
            sx={{
              position: 'fixed',
              right: { xs: 18, sm: 28 },
              bottom: { xs: 18, sm: 28 },
              zIndex: 1200,
              width: 58,
              height: 58,
              borderRadius: '18px',
              color: '#ffffff',
              background: '#111827',
              boxShadow: '0 18px 44px rgba(15, 23, 42, 0.28)',
              '&:hover': { background: '#1f2937' },
            }}
          >
            <HelpOutlineRoundedIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <HelpDrawer
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        contact={contact}
        supportQrUrl={supportQrUrl}
        supportWhatsappUrl={supportWhatsappUrl}
        copy={copy}
        permissionCheckStatus={permissionCheckStatus}
        checkingMediaPermissions={checkingMediaPermissions}
        onOpenPermissions={handleOpenPermissionsFromHelp}
      />
      <LegalDrawer
        open={legalDrawer.open}
        type={legalDrawer.type}
        onClose={() => setLegalDrawer((current) => ({ ...current, open: false }))}
      />
      {permissionDialogOpen && (
        <PermissionDialogHost
          open={permissionDialogOpen}
          onClose={setPermissionDialogOpen}
          onPermissionsGranted={handlePermissionsGrantedFromSheet}
          onSkipPermissions={handlePermissionsSkippedFromSheet}
        />
      )}
      {authOpen && (
        <Suspense fallback={null}>
          <ClubbedAuthModal
            open={authOpen}
            onClose={() => setAuthOpen(false)}
            loginVariant
            onAuthenticated={handleAuthenticated}
            isLeadFormNeeded={false}
          />
        </Suspense>
      )}
    </>
  )
}
